import { TrendingDown, TrendingUp } from "@mui/icons-material";
import { Box, SvgIconProps, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import React from "react";

import { RankingOrder } from "../../../../types/callRanking";

const BACKGROUND_OPACITY = 0.08;
const HOVER_OPACITY = 0.12;
const SELECTED_WEIGHT = 600;
const UNSELECTED_WEIGHT = 400;

interface OrderOptionConfig {
  value: RankingOrder;
  label: string;
  icon: React.ComponentType<SvgIconProps>;
  paletteColor: "success" | "error";
}

const ORDER_OPTIONS: OrderOptionConfig[] = [
  {
    value: RankingOrder.BestFirst,
    label: "Best (Top Performing)",
    icon: TrendingUp,
    paletteColor: "success"
  },
  {
    value: RankingOrder.WorstFirst,
    label: "Worst (Lowest Performing)",
    icon: TrendingDown,
    paletteColor: "error"
  }
];

interface OrderOptionProps {
  option: OrderOptionConfig;
  isSelected: boolean;
}

const OrderOption = ({ option, isSelected }: OrderOptionProps): React.ReactElement => {
  const theme = useTheme();
  const Icon = option.icon;
  const color = theme.palette[option.paletteColor].main;
  return (
    <ToggleButton
      value={option.value}
      sx={{
        textTransform: "none",
        fontWeight: isSelected ? SELECTED_WEIGHT : UNSELECTED_WEIGHT,
        gap: 1,
        ["&.Mui-selected"]: {
          backgroundColor: alpha(color, BACKGROUND_OPACITY),
          color: `${option.paletteColor}.dark`,
          borderColor: `${option.paletteColor}.main`,
          ["&:hover"]: { backgroundColor: alpha(color, HOVER_OPACITY) }
        }
      }}
    >
      <Icon fontSize="small" />
      {option.label}
    </ToggleButton>
  );
};

interface OrderSelectorProps {
  value: RankingOrder | null;
  onChange: (order: RankingOrder) => void;
}

const OrderSelector = ({ value, onChange }: OrderSelectorProps): React.ReactElement => {
  const handleChange = (_: React.MouseEvent<HTMLElement>, newValue: RankingOrder | null): void => {
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Ranking Focus <Typography component="span" color="error.main">*</Typography>
      </Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        fullWidth
        sx={{ height: 48 }}
      >
        {ORDER_OPTIONS.map((option) => (
          <OrderOption
            key={option.value}
            option={option}
            isSelected={value === option.value}
          />
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

export default OrderSelector;
